import React, { ReactElement, useEffect, useMemo, useState, useRef } from "react";
import { Col, Row, Space } from "antd";
import { useSelector } from "react-redux";
import { useGetDashboardConfig } from "@/hooks/AllCustomHooks";
import {
    CA_DD_MODULES,
    RevstackCASectionIds,
    RevstackDashboardModules,
    RevstackDashboardSubModules,
} from "@/ts/common-types";
import { DASHBOARD_TYPES } from "@/ts/common-types";
import RevstackTablePerformanceWidget from "@/components/widgets/RevstackTablePerformanceWidget";
import { useRevstackStreamData } from "@/hooks/useRevstackStreamData";
import {
    resetRevstackDashboardWidgetData,
    updateRevstackDashboardWidgetData,
} from "@/features/revstackWidgetsSlice";
import { NoDataText, WidgetCard } from "@/components/widgets";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import NoteWidget from "@/components/widgets/NoteWidget";

const WIDGET_STYLES = {
    W6_COL_STYLE: {
        borderRight: "1px solid #e8e8e8",
        overflow: "hidden",
        overflowY: "auto" as const,
        padding: "14px",
        height: 500,
        margin: 0,
    },
    HEADER_PADDING: 20,
    TABLE_HEIGHT: 528,
    TABLE_HEIGHT_TABLE_ONLY: "calc(100% - 101px)",
    CARD_HEIGHT_TABLE_ONLY: 480,
} as const;

const RevstackRelativeFiltersLayout = () => {
    const mainFilters = useSelector((state: any) => state.mainFilters.value);
    const mainFiltersData = useSelector(
        (state: any) => state.mainFiltersData.value
    );

    const selectedDateRange = useSelector(
        (state: any) => state.selectedDateRange.value
    );

    const additionalFilters = useSelector((state: any) => state.additionalFilters?.value || {
        tagTypes: [],
        state: {
            isOpen: false,
            appliedFilters: {},
            draftFilters: {},
            activeIndex: 0,
            searchText: "",
        }
    });
    const additionalFiltersState = additionalFilters.state;

    const [noDataDisplayed, setNoDataDisplayed] = useState(false);
    
    // Track the last filter state that triggered an API call
    const lastApiCallFiltersRef = useRef<string | null>(null);

    const skuSelectionConfig = useGetDashboardConfig(
        RevstackDashboardModules.PRODUCT_ANALYSIS,
        DASHBOARD_TYPES.BASE,
        RevstackDashboardSubModules.PRODUCT_SELECTION_LIST
    );

    const revstackWidgetData = useSelector(
        (state: {
            revstackWidgetData: {
                dashboard: any;
                drilldown: any;
                drilldownTrends: any;
            };
        }) => state.revstackWidgetData
    );


    const { infoNoteConfig, nonInfoNoteConfig } = useMemo(() => {
        return skuSelectionConfig?.widgetConfig?.reduce(
            (acc: any, widgetConfig: any) => {
                if (widgetConfig.sectionId === RevstackCASectionIds.INOTE) {
                    acc.infoNoteConfig.push(widgetConfig);
                } else {
                    acc.nonInfoNoteConfig.push(widgetConfig);
                }
                return acc;
            },
            { infoNoteConfig: [], nonInfoNoteConfig: [] }
        );
    }, [skuSelectionConfig]);

    const { getRevstackWidgetStreamData } = useRevstackStreamData({
        config: nonInfoNoteConfig,
        streamConfig: {
            resetWidgetDataAction: resetRevstackDashboardWidgetData,
            updateWidgetDataAction: updateRevstackDashboardWidgetData,
        },
        overviewTabKey: CA_DD_MODULES.OVERVIEW,
    });


    // Only trigger widget request after filters are fetched in RevstackFiltersLayout
    useEffect(() => {
        // Check if filter OPTIONS have been loaded (categories, subcategories, brands arrays)
        // For Product Analysis with SKU mapping, these might be empty arrays but selected should have values
        const hasMainFiltersData =
            mainFiltersData &&
            (mainFiltersData.categories?.length > 0 ||
                mainFiltersData.subcategories?.length > 0 ||
                mainFiltersData.brands?.length > 0 ||
                // For SKU mapping case: check if selected has actual values
                mainFiltersData.selected?.categories?.length > 0 ||
                mainFiltersData.selected?.subcategories?.length > 0 ||
                mainFiltersData.selected?.brands?.length > 0);

        const hasSelectedFilters =
            mainFilters &&
            (mainFilters.categories?.length > 0 ||
                mainFilters.subcategories?.length > 0 ||
                mainFilters.brands?.length > 0);

        const areFiltersReady = hasMainFiltersData && hasSelectedFilters;

        if (areFiltersReady) {
            // Create a unique signature for the current filter state
            const currentFiltersSignature = JSON.stringify({
                mainFilters: {
                    categories: mainFilters.categories,
                    subcategories: mainFilters.subcategories,
                    brands: mainFilters.brands,
                },
                selectedDateRange,
                appliedFilters: additionalFiltersState?.appliedFilters,
            });

            // Only make the API call if filters have actually changed
            if (lastApiCallFiltersRef.current !== currentFiltersSignature) {
                lastApiCallFiltersRef.current = currentFiltersSignature;
                getRevstackWidgetStreamData();
            }
        } else {
            setNoDataDisplayed(true);
        }
    }, [mainFiltersData, mainFilters, selectedDateRange, additionalFiltersState?.appliedFilters]);

    const TableHeaderSection: React.FC<{
        title: string;
        titleIcon: React.ReactNode;
    }> = React.memo(({ title, titleIcon }) => (
        <div className="w-100">
            <Space align="center" direction="horizontal" size={12}>
                <div className="icon-btn">{titleIcon}</div>
                <Space align="center" direction="horizontal" size={12}>
                    <h4 className="mb-0">{title || "Performance Table"}</h4>
                </Space>
            </Space>
        </div>
    ));

    const HeaderSection: React.FC<{
        title: string;
        icon: ReactElement;
        filters: any[];
    }> = ({ title, icon, filters }) => (
        <div
            className="d-flex align-items-center justify-content-between border-bottom"
            style={{ padding: WIDGET_STYLES.HEADER_PADDING }}
        >
            <TableHeaderSection title={title} titleIcon={icon} />
            {buildWidgetCardFilters({ filters })}
        </div>
    );

    return (
        <Row className="w-100">
            <Col span={24} style={{ height: "5rem" }}>
                {infoNoteConfig?.length > 0 && (
                    <div className="mb-3">
                        <NoteWidget widgetConfig={infoNoteConfig?.[0]} />
                    </div>
                )}
                <WidgetCard bodyPaddingClass="px-0 py-0">
                    <HeaderSection
                        title={
                            nonInfoNoteConfig?.[0]?.title ||
                            "Sub Category Performance"
                        }
                        icon={nonInfoNoteConfig?.[0]?.icon}
                        filters={nonInfoNoteConfig?.[0]?.filters}
                    />
                    <RevstackTablePerformanceWidget
                        widgetConfig={nonInfoNoteConfig?.[0]}
                        widgetData={revstackWidgetData.dashboard}
                        title={""}
                        configSessionKey={"revstack-relative-filters-layout"}
                        modalConfig={{
                            onModalOpen: () => {},
                        }}
                    />
                </WidgetCard>
            </Col>
        </Row>
    );
};

export default RevstackRelativeFiltersLayout;
